import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Subject, throttleTime } from 'rxjs'

import { MyProfileService } from '../../pages/my-profile/my-profile.service'
import { AuthService } from './auth.service'

@Injectable({
	providedIn: 'root',
})
export class SessionTimeoutService {
	private timeout: ReturnType<typeof setTimeout> | null = null

	private sessionTimeoutTime = 3600000

	private sessionKey = 'lastActivityTime'

	router = inject(Router)

	authService = inject(AuthService)

	private sessionCheck$ = new Subject<void>()

	profileService = inject(MyProfileService)

	constructor() {
		this.sessionCheck$.pipe(throttleTime(100)).subscribe(() => {
			this.profileService.getMyProfile().subscribe()
		})
	}

	setupSessionTimeout() {
		this.restoreSession()
		this.resetTimeout()
		this.addActivityListeners()
		this.handleTabEvents()
	}

	private addActivityListeners(): void {
		;['click', 'mousemove', 'keydown', 'scroll', 'touchstart'].forEach((event) => {
			window.addEventListener(event, () => this.resetTimeout())
		})
	}

	private saveSessionActivity(): void {
		if (this.sessionTimeoutTime > 0 && this.authService.getAccessToken()) {
			const currentTime = Date.now()
			sessionStorage.setItem(this.sessionKey, currentTime.toString())
		}
	}

	private resetTimeout(): void {
		if (this.timeout) {
			clearTimeout(this.timeout)
		}
		this.saveSessionActivity()
		if (this.sessionTimeoutTime > 0 && this.authService.getAccessToken()) {
			this.timeout = setTimeout(() => {
				this.logout()
			}, this.sessionTimeoutTime)
		}
	}

	private restoreSession(): void {
		const lastActivity = sessionStorage.getItem(this.sessionKey)
		if (lastActivity) {
			const elapsedTime = Date.now() - parseInt(lastActivity, 10)
			if (elapsedTime > this.sessionTimeoutTime) {
				this.logout()
			}
		}
	}

	private handleTabEvents(): void {
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') {
				this.restoreSession()
				this.resetTimeout()
				this.checkUserSession()
			}
		})

		window.addEventListener('focus', () => {
			this.checkUserSession()
		})

		window.addEventListener('beforeunload', () => {
			this.saveSessionActivity()
		})
	}

	private checkUserSession(): void {
		this.sessionCheck$.next()
	}

	private logout() {
		this.authService.logOut()
	}
}
